import { Fragment, useRef, type KeyboardEvent } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/components/ui';
import type { StepStatus, WorkflowStep, WorkflowStepId } from './workflowSteps';

interface WorkflowStepperProps {
  steps: WorkflowStep[];
  activeStep: WorkflowStepId;
  onStepChange: (step: WorkflowStepId) => void;
}

const STATUS_LABEL: Record<StepStatus, string> = {
  todo: '',
  done: 'complete',
  attention: 'needs attention',
};

const StepIndicator = ({
  status,
  index,
  active,
}: {
  status: StepStatus;
  index: number;
  active: boolean;
}) => (
  <span
    aria-hidden="true"
    className={cn(
      'flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums transition-colors duration-150',
      status === 'done' && 'bg-accent text-accent-fg',
      status === 'attention' &&
        'bg-warning-muted text-warning-fg ring-1 ring-inset ring-warning/50',
      status === 'todo' &&
        (active
          ? 'text-accent ring-[1.5px] ring-inset ring-accent'
          : 'text-fg-muted ring-1 ring-inset ring-border-strong')
    )}
  >
    {status === 'done' ? (
      <Check className="size-3.5" strokeWidth={3} />
    ) : status === 'attention' ? (
      '!'
    ) : (
      index + 1
    )}
  </span>
);

/**
 * Import → Adjust → Export. Tabs with automatic activation: Left/Right move
 * and select, Home/End jump to the ends. Each step shows its live status.
 * On phones only the current step shows its label and detail.
 */
export const WorkflowStepper = ({
  steps,
  activeStep,
  onStepChange,
}: WorkflowStepperProps) => {
  const tabs = useRef<Array<HTMLButtonElement | null>>([]);

  const handleKeyDown = (event: KeyboardEvent, index: number) => {
    const last = steps.length - 1;
    let next: number;
    switch (event.key) {
      case 'ArrowRight':
        next = index === last ? 0 : index + 1;
        break;
      case 'ArrowLeft':
        next = index === 0 ? last : index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }
    event.preventDefault();
    onStepChange(steps[next].id);
    tabs.current[next]?.focus();
  };

  return (
    <div className="border-b border-border bg-surface">
      <div
        role="tablist"
        aria-label="Workflow"
        className="mx-auto flex h-12 max-w-screen-2xl items-stretch justify-center px-4 sm:h-14 sm:px-6"
      >
        {steps.map((step, index) => {
          const active = step.id === activeStep;
          const previousDone = index > 0 && steps[index - 1].status === 'done';
          return (
            <Fragment key={step.id}>
              {index > 0 && (
                <span
                  aria-hidden="true"
                  className={cn(
                    'mx-1.5 h-px w-5 shrink-0 self-center sm:mx-3 sm:w-12 lg:w-20',
                    previousDone ? 'bg-accent/40' : 'bg-border-strong/70'
                  )}
                />
              )}
              <button
                ref={(element) => {
                  tabs.current[index] = element;
                }}
                type="button"
                role="tab"
                id={`tab-${step.id}`}
                aria-controls={`panel-${step.id}`}
                aria-selected={active}
                tabIndex={active ? 0 : -1}
                onClick={() => onStepChange(step.id)}
                onKeyDown={(event) => handleKeyDown(event, index)}
                className={cn(
                  'group relative flex min-w-0 items-center gap-2.5 rounded-md px-1.5 outline-offset-[-2px] transition-colors duration-150 sm:px-2.5',
                  // Current-step underline, aligned with the band's bottom border.
                  'after:absolute after:inset-x-1.5 after:-bottom-px after:h-0.5 after:rounded-full after:transition-colors sm:after:inset-x-2.5',
                  active ? 'after:bg-accent' : 'after:bg-transparent'
                )}
              >
                <StepIndicator
                  status={step.status}
                  index={index}
                  active={active}
                />
                <span
                  className={cn(
                    'min-w-0 flex-col items-start text-left leading-tight',
                    active ? 'flex' : 'sr-only sm:not-sr-only sm:flex'
                  )}
                >
                  <span
                    className={cn(
                      'text-sm font-medium transition-colors duration-150',
                      active ? 'text-fg' : 'text-fg-muted group-hover:text-fg'
                    )}
                  >
                    {step.label}
                    {STATUS_LABEL[step.status] && (
                      <span className="sr-only">
                        {`, ${STATUS_LABEL[step.status]}`}
                      </span>
                    )}
                  </span>
                  {step.detail && (
                    <span
                      className={cn(
                        'mt-0.5 max-w-[11rem] truncate text-xs tabular-nums sm:max-w-[14rem]',
                        step.status === 'attention'
                          ? 'text-warning-fg'
                          : 'text-fg-muted'
                      )}
                    >
                      {step.detail}
                    </span>
                  )}
                </span>
              </button>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};
